import type { ReactElement } from "react"
import styled from "styled-components"
import { type DataSource, useProvenance } from "./ProvenanceContext"

const Root = styled.aside`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background-color: ${({ theme }) => theme.color.surface};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
`

const Key = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

/** The same dashed outline the overlay draws, at swatch size, so the key is the mark itself. */
const Swatch = styled.span<{ $source: DataSource }>`
  width: ${({ theme }) => theme.spacing.lg};
  height: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radius.sm};
  outline: 2px dashed
    ${({ theme, $source }) => ($source === "dce" ? theme.color.provenanceDce : theme.color.provenanceMlc)};
  outline-offset: -2px;
`

const Name = styled.span`
  color: ${({ theme }) => theme.color.textPrimary};
`

/**
 * The overlay's key. Two dashed outlines on a page are not self-explanatory —
 * without this, the demo shows that the page is assembled from two systems
 * without saying which is which, and the whole point of proposal 03 is which.
 *
 * Renders only while the overlay is on, and reads the same context the outlines
 * do, so it cannot disagree with what is on the cards.
 */
export const ProvenanceLegend = (): ReactElement | null => {
  const { isOn } = useProvenance()

  if (!isOn) {
    return null
  }

  return (
    <Root data-qa-hook="provenance-legend">
      <Key data-qa-hook="provenance-legend-dce">
        <Swatch $source="dce" aria-hidden="true" />
        <Name>DCE graph</Name>
        {" — libraryV2"}
      </Key>
      <Key data-qa-hook="provenance-legend-mlc">
        <Swatch $source="mlc" aria-hidden="true" />
        <Name>myLifeway</Name>
        {" — seats, windows and product art"}
      </Key>
      Every outlined element is drawn by the system it came from.
    </Root>
  )
}
